import React from 'react';
import { DivideIcon as LucideIcon } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
}

/**
 * Design System Button Component
 * 
 * Variants:
 * - Primary: High contrast, main actions (WCAG AAA compliant)
 * - Secondary: Medium contrast, secondary actions
 * - Tertiary: Low contrast, subtle actions
 * - Ghost: Transparent, minimal actions
 * - Danger: Error/destructive actions
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}) => {
  // Base styles - consistent across all variants
  const baseStyles = `
    inline-flex items-center justify-center font-medium rounded-lg
    transition-all duration-200 ease-out font-semibold
    focus:outline-none focus:ring-2 focus:ring-offset-1
    disabled:cursor-not-allowed disabled:opacity-50
    active:scale-[0.98] transform
    ${fullWidth ? 'w-full' : ''}
  `;

  // Size variants
  const sizeStyles = {
    sm: 'px-4 py-2 text-sm gap-2 h-9',
    md: 'px-6 py-3 text-sm gap-2 h-11',
    lg: 'px-8 py-4 text-base gap-3 h-12',
  };

  // Color variants with accessibility considerations
  const variantStyles = {
    primary: `
      bg-blue-500 text-white
      hover:bg-blue-600 hover:scale-105
      focus:ring-blue-500/30
      disabled:bg-slate_gray disabled:scale-100
    `,
    secondary: `
      bg-white text-eerie_black border-2 border-platinum
      hover:bg-anti_flash_white hover:border-french_gray
      focus:ring-slate_gray/20 focus:border-slate_gray
      disabled:bg-platinum disabled:text-slate_gray disabled:border-french_gray
    `,
    tertiary: `
      bg-transparent text-slate_gray border border-french_gray
      hover:bg-anti_flash_white hover:text-outer_space hover:border-slate_gray
      focus:ring-slate_gray/20 focus:border-slate_gray
      disabled:text-french_gray_2 disabled:border-platinum
    `,
    ghost: `
      bg-transparent text-slate_gray
      hover:bg-anti_flash_white hover:text-outer_space
      focus:ring-slate_gray/20
      disabled:text-french_gray_2
    `,
    danger: `
      bg-red-500 text-white
      hover:bg-red-600 hover:scale-105
      focus:ring-red-500/30
      disabled:bg-slate_gray disabled:scale-100
    `,
  };

  const combinedClassName = `
    ${baseStyles}
    ${sizeStyles[size]}
    ${variantStyles[variant]}
    ${className}
  `.trim();

  return (
    <button
      className={combinedClassName}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-1" />
      )}
      
      {Icon && iconPosition === 'left' && !loading && (
        <Icon className={`${size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'}`} />
      )}
      
      {children}
      
      {Icon && iconPosition === 'right' && !loading && (
        <Icon className={`${size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'}`} />
      )}
    </button>
  );
};